import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  ArrowRightIcon,
  BanknotesIcon,
  BookOpenIcon,
  CheckCircleIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  InformationCircleIcon,
  UserGroupIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { fetchUser } from "../../../api/users";
import {
  USER_PROFILE_GUIDE_FIELDS,
  normalizeUserRecord,
  userHasChildren,
  userProfileIssues,
} from "./userSetupStatus";
import { useEmailSignatureNagAudience } from "./useEmailSignatureNagAudience";

const GuideTaskRow = ({ icon: Icon, title, status, description, to, linkLabel }) => {
  const tone =
    status === "ok"
      ? "border-emerald-200 bg-emerald-50/50"
      : status === "warn"
        ? "border-amber-200 bg-amber-50/40"
        : "border-slate-200 bg-slate-50/60";

  const StatusIcon =
    status === "ok" ? CheckCircleIcon : status === "warn" ? ExclamationTriangleIcon : InformationCircleIcon;

  const statusColor =
    status === "ok" ? "text-emerald-700" : status === "warn" ? "text-amber-800" : "text-slate-600";

  return (
    <div
      className={`flex flex-col gap-3 rounded-2xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${tone}`}
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className={`mt-0.5 flex items-center gap-1.5 text-xs font-medium ${statusColor}`}>
            <StatusIcon className="h-4 w-4 shrink-0" />
            <span>{description}</span>
          </div>
        </div>
      </div>
      {to ? (
        <Link
          to={to}
          className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
        >
          {linkLabel}
          <ArrowRightIcon className="h-4 w-4" />
        </Link>
      ) : null}
    </div>
  );
};

/**
 * Personlig oppstartsstatus / sjekkliste for innlogget bruker (ansatt + innleid).
 * Vises på brukerguiden (profil), ikke direkte på dashbordets forside.
 */
export const UserGuideProfileSection = () => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const { eligible: showEmailSignature } = useEmailSignatureNagAudience();
  const [user, setUser] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;

    (async () => {
      setLoadError(false);
      try {
        const res = await fetchUser(currentUser.id, { fields: USER_PROFILE_GUIDE_FIELDS });
        if (cancelled) return;
        const normalized = normalizeUserRecord(res) ?? res;
        const ok = normalized && typeof normalized === "object" && normalized.id != null && !normalized.errors;
        if (!ok) {
          setLoadError(true);
          setUser(null);
        } else {
          setUser(normalized);
        }
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setUser(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  const loaded = user !== null;
  const issueSet = useMemo(
    () => new Set(loaded ? userProfileIssues(user) : []),
    [user, loaded]
  );
  const hasChildren = useMemo(() => (loaded ? userHasChildren(user) : false), [user, loaded]);

  const tasks = useMemo(() => {
    if (!loaded) return [];

    const required = (id, icon) => ({
      id,
      icon,
      title: t(`user_guide.profile.task.${id}.title`),
      status: issueSet.has(id) ? "warn" : "ok",
      description: t(
        issueSet.has(id) ? `user_guide.profile.task.${id}.warn` : `user_guide.profile.task.${id}.ok`
      ),
      to: "/dashboard/user/profile",
      linkLabel: t("user_guide.profile.link.profile"),
    });

    return [
      required("mobile", DevicePhoneMobileIcon),
      required("next_of_kin", UsersIcon),
      required("social_security", IdentificationIcon),
      required("account_number", BanknotesIcon),
      ...(showEmailSignature
        ? [
            {
              ...required("email_signature", EnvelopeIcon),
              to: "/dashboard/user/profile#email",
            },
          ]
        : []),
      {
        id: "children",
        icon: UserGroupIcon,
        status: "info",
        title: t("user_guide.profile.task.children.title"),
        description: hasChildren
          ? t("user_guide.profile.task.children.ok")
          : t("user_guide.profile.task.children.info"),
        to: "/dashboard/user/profile",
        linkLabel: t("user_guide.profile.link.profile"),
      },
    ];
  }, [issueSet, hasChildren, loaded, showEmailSignature, t]);

  const requiredTasks = tasks.filter((row) => row.status !== "info");
  const doneCount = requiredTasks.filter((r) => r.status === "ok").length;
  const warnCount = requiredTasks.filter((r) => r.status === "warn").length;

  return (
    <section
      id="profile-setup-status"
      className="scroll-mt-24 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-5 sm:px-5"
    >
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 shadow-sm">
            <BookOpenIcon className="h-4 w-4" />
            {t("user_guide.profile.badge")}
          </div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-950">
            {t("user_guide.profile.heading")}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">{t("user_guide.profile.subtitle")}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
            {t("user_guide.profile.progress_label")}
          </div>
          <div className="mt-1 font-semibold text-slate-900">
            {loadError
              ? t("user_guide.profile.progress_error")
              : user === null
                ? t("user_guide.profile.progress_loading")
                : warnCount > 0
                  ? t("user_guide.profile.progress_warnings", {
                      done: doneCount,
                      total: requiredTasks.length,
                      warnings: warnCount,
                    })
                  : t("user_guide.profile.progress_ok", {
                      done: doneCount,
                      total: requiredTasks.length,
                    })}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {!loaded && !loadError ? (
          <p className="text-sm text-slate-500">{t("user_guide.profile.progress_loading")}</p>
        ) : null}
        {tasks.map((row) => (
          <GuideTaskRow key={row.id} {...row} />
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">
        <p>{t("user_guide.profile.footer_hint")}</p>
      </div>
    </section>
  );
};
